import {ChangeDetectionStrategy, Component, Inject} from '@angular/core';
import {Device} from '../../types/devices/device';
import {DeviceSlot} from '../../types/devices/device-slot';
import {DeviceSlotReservation} from '../../types/devices/device-slot-reservation';
import {ProjectId} from '../../types/ids';
import {Project} from '../../types/interfaces/project.interface';
import {UserInfo} from '../../types/user/user-info';
import {TOOL_CONTAINER_DATA} from '../../tools/modaltoolcontainer/modaltoolcontainer.component';
import {WebmateToolConfig} from '../../tools/modaltoolcontainer/modaltoolcontainer.service';

interface DeviceSlotDetailEntry {
    key: string;
    value: string;
    copyValue?: string;
}

interface DeviceSlotDetailGroup {
    name: string;
    entries: DeviceSlotDetailEntry[];
}

interface JsonTableEntry {
    key: string;
    value: string;
    isArrayItem?: boolean;
}

export class DeviceSlotDetailsToolConfig implements WebmateToolConfig {
    toolName = 'Device Slot Details';
    scrollable = true;

    constructor(public deviceSlot: DeviceSlot,
                public projectId: ProjectId,
                public deviceInSlot: Device | null,
                public deviceIsFromDifferentProject: boolean,
                public projectBlockingSlot?: Project,
                public userBlockingSlot?: UserInfo,
                public shownReservation?: DeviceSlotReservation,
                public isReservedByMyself = false,
                public isReservedByAnotherUser = false,
                public userHavingReservation?: UserInfo,
                public upcomingReservations: DeviceSlotReservation[] = [],
                public usersByReservationId: {[reservationId: string]: UserInfo} = {}) {}
}

@Component({
    selector: 'wm-device-slot-details',
    templateUrl: './device-slot-details.component.html',
    styleUrls: ['./device-details.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceSlotDetailsComponent {
    readonly groups: DeviceSlotDetailGroup[];

    constructor(@Inject(TOOL_CONTAINER_DATA) protected readonly config: DeviceSlotDetailsToolConfig) {
        this.groups = this.buildGroups();
    }

    getJsonTableEntries(value: string): JsonTableEntry[] | undefined {
        try {
            const parsed: unknown = JSON.parse(value);
            if (parsed === null || typeof parsed !== 'object') {
                return undefined;
            }
            const entries: JsonTableEntry[] = [];
            for (const [key, entryValue] of Object.entries(parsed as Record<string, unknown>)) {
                if (Array.isArray(entryValue)) {
                    entryValue.forEach((item, index) => {
                        entries.push({
                            key: index === 0 ? key : '',
                            value: item !== null && typeof item === 'object' ? JSON.stringify(item) : String(item),
                            isArrayItem: true
                        });
                    });
                } else {
                    entries.push({
                        key,
                        value: entryValue !== null && typeof entryValue === 'object' ? JSON.stringify(entryValue) : String(entryValue)
                    });
                }
            }
            return entries;
        } catch {
            return undefined;
        }
    }

    private buildGroups(): DeviceSlotDetailGroup[] {
        const groups: DeviceSlotDetailGroup[] = [{
            name: 'General',
            entries: [
                {key: 'name', value: this.config.deviceSlot.name},
                {key: 'id', value: this.config.deviceSlot.id, copyValue: this.config.deviceSlot.id},
                {
                    key: 'daily redeployment',
                    value: this.config.deviceSlot.isAutoRedeployEnabledForProject(this.config.projectId)
                        ? 'Any device inside this slot will be redeployed daily'
                        : 'No automatic redeployment of devices inside this slot'
                }
            ]
        }];

        const usageEntries = this.getUsageEntries();
        if (usageEntries.length > 0) {
            groups.push({name: 'Usage', entries: usageEntries});
        }

        const reservationEntries = this.getReservationEntries();
        if (reservationEntries.length > 0) {
            groups.push({name: 'Reservations', entries: reservationEntries});
        }

        const networkEntries = this.getNetworkEntries();
        if (networkEntries.length > 0) {
            groups.push({name: 'Network', entries: networkEntries});
        }

        if (this.config.deviceSlot.deviceRequirement !== undefined) {
            groups.push({
                name: 'Requirements',
                entries: [{key: 'device requirements', value: JSON.stringify(this.config.deviceSlot.deviceRequirement)}]
            });
        }

        return groups;
    }

    private getUsageEntries(): DeviceSlotDetailEntry[] {
        if (this.config.deviceSlot.slotIsBeingReleased || !this.config.userBlockingSlot || !this.config.projectBlockingSlot) {
            return [];
        }
        if (!this.config.deviceInSlot) {
            return [{key: 'blocked by', value: `Unknown device created by ${this.config.userBlockingSlot.name} in a different project`}];
        }

        const device = this.config.deviceInSlot;
        const project = this.config.deviceIsFromDifferentProject ? ` in project ${this.config.projectBlockingSlot.name}` : ' in this project';
        return [{
            key: 'blocked by',
            value: `${device.name} (${device.id})${project}, created by ${this.config.userBlockingSlot.name}`
        }];
    }

    private getReservationEntries(): DeviceSlotDetailEntry[] {
        const entries: DeviceSlotDetailEntry[] = [];
        if (this.config.shownReservation && (this.config.isReservedByMyself || this.config.isReservedByAnotherUser)) {
            const user = this.config.isReservedByMyself ? 'You' : this.config.userHavingReservation?.name ?? 'Unknown user';
            entries.push({key: 'currently reserved by', value: `${user} ${this.config.shownReservation.toNiceString()}`});
        }
        this.config.upcomingReservations.forEach(reservation => {
            const user = this.config.usersByReservationId[reservation.id]?.name ?? 'Unknown user';
            entries.push({key: 'upcoming reservation', value: `${user} ${reservation.toNiceString()}`});
        });
        return entries;
    }

    private getNetworkEntries(): DeviceSlotDetailEntry[] {
        const permanentProperties = this.config.deviceSlot.propertyBag.permanent_properties;
        const entries: DeviceSlotDetailEntry[] = [];
        if (permanentProperties?.ip !== undefined) {
            entries.push({key: 'ip', value: String(permanentProperties.ip)});
        }
        if (permanentProperties?.proxy !== undefined) {
            const proxy = permanentProperties.proxy;
            entries.push({
                key: 'proxy',
                value: `${proxy.baseURL} on port ${proxy.proxyPort} (${proxy.permanent ? 'permanent' : 'non-permanent'})`
            });
        }
        return entries;
    }
}
